import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Layout = ({ children }) => {
  const { user, logout, isPlatformAdmin, isCustomer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const adminNav = [
    { path:'/dashboard', icon:'⬡', label:'Dashboard' },
    { path:'/vendors', icon:'◈', label:'Vendors' },
    { path:'/requirements', icon:'◇', label:'Requirements' },
  ];

  const vendorNav = [
    { path:'/dashboard', icon:'⬡', label:'Dashboard' },
    { path:'/tickets', icon:'◈', label:'Tickets' },
    { path:'/requirements', icon:'⚑', label:'Requirements' },
    { path:'/engineers', icon:'◉', label:'Engineers' },
    { path:'/freelancers', icon:'◇', label:'Freelancers' },
    { path:'/customers', icon:'◫', label:'Customers' },
    { path:'/inventory', icon:'⊞', label:'Inventory' },
    { path:'/map', icon:'◎', label:'Field Activity' },
    { path:'/reports', icon:'≡', label:'Reports' },
  ];

  const customerNav = [
    { path:'/dashboard', icon:'⚑', label:'My Requirements' },
  ];

  const navItems = isPlatformAdmin
    ? adminNav
    : isCustomer
    ? customerNav
    : vendorNav;

  const initials = user?.name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase() || 'U';

  return (
    <div style={{
      display:'flex', height:'100vh',
      background:'var(--bg)'
    }}>
      {/* SIDEBAR */}
      <aside style={{
        width: collapsed ? '60px' : '220px',
        minWidth: collapsed ? '60px' : '220px',
        background:'var(--surface)',
        borderRight:'1px solid var(--border)',
        display:'flex', flexDirection:'column',
        transition:'all 0.2s',
        overflow:'hidden',
      }}>
        {/* Logo */}
        <div style={{
          padding:'20px 16px',
          borderBottom:'1px solid var(--border)',
          display:'flex', alignItems:'center',
          gap:'10px',
        }}>
          <div style={{
            width:'36px', minWidth:'36px',
            height:'36px',
            borderRadius:'8px',
            display:'flex', alignItems:'center',
            justifyContent:'center',
            cursor:'pointer',
            overflow:'hidden',
          }} onClick={() => setCollapsed(!collapsed)}>
            <img
              src="/logo192.png"
              alt="IRONWALL"
              style={{
                width:'100%', height:'100%',
                objectFit:'contain',
              }}
            />
          </div>
          {!collapsed && (
            <div>
              <div style={{
                fontFamily:'Syne,sans-serif',
                fontSize:'14px', fontWeight:'800',
              }}>
                IRONWALL
              </div>
              <div style={{
                fontSize:'9px',
                color:'var(--text3)',
                fontFamily:'DM Mono,monospace',
                letterSpacing:'1px',
              }}>
                {isPlatformAdmin
                  ? 'COMMAND CENTER'
                  : isCustomer
                  ? 'CUSTOMER PORTAL'
                  : 'SERVICE BRIDGE'}
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ padding:'12px 8px', flex:1 }}>
          {navItems.map(item => {
            const active =
              location.pathname === item.path;
            return (
              <div key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  display:'flex',
                  alignItems:'center',
                  gap:'10px',
                  padding:'9px 12px',
                  borderRadius:'8px',
                  cursor:'pointer',
                  marginBottom:'2px',
                  background: active
                    ? 'rgba(0,212,255,0.1)' : 'transparent',
                  color: active
                    ? 'var(--accent)' : 'var(--text2)',
                  borderLeft: active
                    ? '3px solid var(--accent)'
                    : '3px solid transparent',
                  transition:'all 0.15s',
                  fontSize:'13.5px',
                  fontWeight:'500',
                  whiteSpace:'nowrap',
                }}
                onMouseEnter={e => {
                  if (!active) {
                    e.currentTarget.style.background =
                      'var(--surface2)';
                    e.currentTarget.style.color =
                      'var(--text)';
                  }
                }}
                onMouseLeave={e => {
                  if (!active) {
                    e.currentTarget.style.background =
                      'transparent';
                    e.currentTarget.style.color =
                      'var(--text2)';
                  }
                }}
              >
                <span style={{
                  fontSize:'16px',
                  minWidth:'18px',
                  textAlign:'center',
                }}>
                  {item.icon}
                </span>
                {!collapsed && item.label}
              </div>
            );
          })}
        </nav>

        {/* Guide Download */}
        {!isPlatformAdmin && !isCustomer && (
          <div style={{
            padding:'0 12px 10px',
          }}>
            <a
              href="/Ironwall_Servicebridge_Guide.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display:'flex',
                alignItems:'center',
                gap:'10px',
                padding:'9px 12px',
                borderRadius:'8px',
                fontSize:'12.5px',
                fontWeight:'500',
                color:'var(--text3)',
                textDecoration:'none',
                border:'1px dashed var(--border2)',
                whiteSpace:'nowrap',
                overflow:'hidden',
              }}
            >
              <span style={{
                fontSize:'15px',
                minWidth:'18px',
                textAlign:'center',
              }}>
                📄
              </span>
              {!collapsed && 'Download Guide'}
            </a>
          </div>
        )}

        {/* User */}
        <div style={{
          padding:'12px',
          borderTop:'1px solid var(--border)',
        }}>
          <div style={{
            display:'flex', alignItems:'center',
            gap:'10px',
            padding:'10px 8px',
            borderRadius:'8px',
            background:'var(--surface2)',
          }}>
            <div style={{
              width:'30px', minWidth:'30px',
              height:'30px', borderRadius:'50%',
              background:
                'linear-gradient(135deg,var(--accent),var(--purple))',
              display:'flex', alignItems:'center',
              justifyContent:'center',
              fontSize:'11px', fontWeight:'700',
              color:'var(--bg)',
            }}>
              {initials}
            </div>
            {!collapsed && (
              <div style={{ flex:1, overflow:'hidden' }}>
                <div style={{
                  fontSize:'12px', fontWeight:'600',
                  whiteSpace:'nowrap',
                  overflow:'hidden',
                  textOverflow:'ellipsis',
                }}>
                  {user?.name}
                </div>
                <div style={{
                  display:'flex', gap:'8px',
                  fontSize:'10px',
                  fontFamily:'DM Mono,monospace',
                }}>
                  {!isPlatformAdmin && !isCustomer && (
                    <span
                      onClick={() => navigate('/account')}
                      style={{
                        color:'var(--accent)',
                        cursor:'pointer',
                      }}
                    >
                      My Account
                    </span>
                  )}
                  <span
                    onClick={logout}
                    style={{
                      color:'var(--text3)',
                      cursor:'pointer',
                    }}
                  >
                    Logout
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <main style={{
        flex:1, overflow:'auto',
        display:'flex', flexDirection:'column',
      }}>
        {children}
      </main>
    </div>
  );
};

export default Layout;